// MonthCalendar/index.tsx
import { Flex, Grid } from "@chakra-ui/react";
import moment from "moment";
import { useMemo } from "react";
import DayCell from "./DayCell";
import { CalendarEvent } from "../types";
import { buildEventGroupsByDate } from "./helpers";

interface MonthCalendarProps {
  visibleMonth: moment.Moment;
  events: CalendarEvent[];
  timezone: string;
  showWeekends: boolean;
  handleClick: (event: CalendarEvent) => void;
}

export default function MonthCalendar({
  visibleMonth,
  events,
  timezone,
  showWeekends,
  handleClick,
}: MonthCalendarProps) {
  const startOfMonth = visibleMonth.clone().startOf("month");
  const endOfMonth = visibleMonth.clone().endOf("month");

  // Use ISO week (Monday start) when showing weekends, regular week otherwise
  const gridStart = showWeekends
    ? startOfMonth.clone().startOf("isoWeek")
    : startOfMonth.clone().startOf("week");
  const gridEnd = showWeekends
    ? endOfMonth.clone().endOf("isoWeek")
    : endOfMonth.clone().endOf("week");

  const allDays: moment.Moment[] = [];
  const curr = gridStart.clone();
  while (curr.isSameOrBefore(gridEnd)) {
    allDays.push(curr.clone());
    curr.add(1, "day");
  }

  const filteredDays = showWeekends
    ? allDays
    : allDays.filter((d) => ![0, 6].includes(d.day())); // 0 = Sunday, 6 = Saturday

  const headers = showWeekends
    ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri"];

  const groupedEventsByDate = useMemo(() => buildEventGroupsByDate(events, timezone), [events, timezone]);

  return (
    <Flex direction="column">
      {/* Day headers */}
      <Grid
        templateColumns={`repeat(${headers.length}, minmax(0, 1fr))`} 
        border="1px solid"
        borderColor="gray.200"
        borderBottom="none"
        borderTopRadius="lg"
      >
        {headers.map((d, index) => (
          <Flex
            key={d}
            justify="center"
            p="3"
            bg="gray.50"
            borderRight="1px solid"
            borderColor="gray.200"
            _last={{ borderRight: "none" }}
            borderTopLeftRadius={index === 0 ? "lg" : "none"}
            borderTopRightRadius={index === headers.length - 1 ? "lg" : "none"}
            fontWeight="medium"
            fontSize="sm"
            color="gray.700"
          >
            {d}
          </Flex>
        ))}
      </Grid>

      {/* Calendar grid */}
      <Grid 
        templateColumns={`repeat(${headers.length}, minmax(0, 1fr))`}
        border="1px solid"
        borderColor="gray.200"
        borderBottomRadius="lg"
      >
        {filteredDays.map((day, index) => {
          const dateKey = day.format("YYYY-MM-DD");
          const groupedEventsByDay = groupedEventsByDate.get(dateKey);

          // Convert Map to array of CalendarEventGroup
          const dayGroupedEvents = groupedEventsByDay
            ? Array.from(groupedEventsByDay.values())
            : [];

          const isOutsideMonth = !day.isSame(visibleMonth, "month");

          // Calculate position in grid
          const colsPerRow = headers.length;
          const isFirstInRow = index % colsPerRow === 0;
          const isLastInRow = (index + 1) % colsPerRow === 0;
          const totalRows = Math.ceil(filteredDays.length / colsPerRow);
          const currentRow = Math.floor(index / colsPerRow);
          const isLastRow = currentRow === totalRows - 1;

          return (
            <DayCell
              key={dateKey}
              day={day}
              dateKey={dateKey}
              isOutsideMonth={isOutsideMonth}
              eventGroups={dayGroupedEvents}
              timezone={timezone}
              onEventClick={handleClick}
              isFirstInRow={isFirstInRow}
              isLastInRow={isLastInRow}
              isLastRow={isLastRow}
            />
          );
        })}
      </Grid>
    </Flex>
  );
}
